/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import SmartUserPicker, { type OptionData } from '@atlaskit/smart-user-picker';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { BulkIssueSelector } from '../../common/ui/issue-selector';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	negativeMarginTop: {
		// This is rendered inside a Box with default top padding within `StoreAssistantView`
		// This action does not have render a response from the assistant, so we need to negate this extra padding
		marginTop: token('space.negative.150'),
	},
	wrapper: {
		flex: 1,
		minWidth: '0px',
	},
});

const JiraIssueAssignActionNewLayout = ({
	actions,
	getReadonly,
	onCustomDataConfirm,
	cloudId,
	onActioned,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.JiraIssueAssign>): JSX.Element => {
	di(BulkIssueSelector);
	di(SmartUserPicker);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [selectedAssignee, setSelectedAssignee] = useState<OptionData | undefined>(undefined);
	const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	//Use assignee id of first action as default assignee id
	const defaultAssigneeId = actions[0].data.assignee;
	const allActionsAreReadonly = actions.every((action) => getReadonly(action));
	const initialIssues = actions.map((action) => {
		return {
			issueKey: action.data.key,
			readonly: getReadonly(action),
			url: action.data.url,
		};
	});
	const handleSubmit = useCallback(async () => {
		if (!selectedIssues.length || allActionsAreReadonly) {
			return;
		}
		let actionsToSubmit: ActionWith<
			ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueAssign]
		>[] = [];
		setIsLoading(true);
		try {
			const actionsMap = new Map(actions.map((action) => [action.data.key, action]));
			actionsToSubmit = selectedIssues
				.map((issueKey) => actionsMap.get(issueKey))
				.filter(
					(
						action,
					): action is ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueAssign]> =>
						action !== undefined,
				)
				.filter((action) => !getReadonly(action));

			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const plannedActions = actions.filter((action) => action.status === 'planned');
				const actionsToSubmitKeys = new Set(actionsToSubmit.map((action) => action.data.key));

				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'jiraIssueAssignRenderer',
					},
					confirmed_tools: {
						message_id: messageId,
						tools: plannedActions.map((action) =>
							actionsToSubmitKeys.has(action.data.key)
								? buildToolFromAction(action, 'confirmed', {
										key: action.data.key,
										assignee: selectedAssignee?.id || defaultAssigneeId,
										url: action.data.url,
										ari: action.data.ari,
									})
								: buildToolFromAction(action, 'cancelled'),
						),
					},
				});
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'rovoJiraIssueAssignSubmit',
					attributes: {
						issueCount: actionsToSubmit.length,
					},
				});
				return;
			}

			const submissionPromises = actionsToSubmit.map(async (action) => {
				await onCustomDataConfirm(action, {
					key: action.data.key,
					assignee: selectedAssignee?.id || defaultAssigneeId,
					url: action.data.url,
					ari: action.data.ari,
				});
			});

			await Promise.all(submissionPromises);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoJiraIssueAssignSubmit',
				attributes: {
					issueCount: actionsToSubmit.length,
				},
			});
		} catch (e) {
			// TODO: remove catch block once resumption is cleaned up
			if (e instanceof Error) {
				actionsToSubmit.forEach((action) => {
					onActioned(action, e.message);
				});
			} else {
				actionsToSubmit.forEach((action) => {
					onActioned(action, formatMessage(messages.jiraBulkIssueAssignErrorMessage));
				});
			}
		} finally {
			setIsLoading(false);
		}
	}, [
		selectedIssues,
		allActionsAreReadonly,
		actions,
		sendAnalyticsEvent,
		getReadonly,
		onCustomDataConfirm,
		selectedAssignee?.id,
		defaultAssigneeId,
		onActioned,
		formatMessage,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	]);

	const handleCancel = useCallback(() => {
		if (
			!FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			return;
		}
		const plannedActions = actions.filter((action) => action.status === 'planned');
		if (!plannedActions.length) {
			return;
		}
		sendMessageConfirmedTools({
			agentId: recipientAgentId,
			conversationId,
			messageSources: {
				interactionSource: 'jiraIssueAssignRenderer',
			},
			confirmed_tools: {
				message_id: messageId,
				tools: plannedActions.map((action) => buildToolFromAction(action, 'cancelled')),
			},
		});
	}, [actions, sendMessageConfirmedTools, recipientAgentId, conversationId, messageId]);

	const handleSelectIssue = useCallback((issueKeys: string[]) => {
		setSelectedIssues(issueKeys);
	}, []);
	return (
		<Stack space="space.200" xcss={styles.negativeMarginTop}>
			<Text>{formatMessage(messages.jiraIssueAssignTitle)}</Text>
			<ActionCardWrapper
				title={formatMessage(messages.jiraIssueAssignActionCardTitle)}
				product="jira"
			>
				<Stack space="space.150">
					<Inline alignBlock="center" space="space.150">
						<Text weight="semibold">{formatMessage(messages.jiraIssueAssigneeTitle)}</Text>
						<Box xcss={styles.wrapper}>
							<SmartUserPicker
								defaultValue={{ id: defaultAssigneeId, type: 'user' }}
								isClearable={false}
								prefetch
								fieldId="assignee"
								productKey="people"
								siteId={cloudId}
								width="100%"
								includeGroups={false}
								includeTeams={false}
								value={selectedAssignee}
								onChange={(newValue) => {
									setSelectedAssignee(newValue as OptionData);
								}}
								appearance="compact"
								maxOptions={10}
							/>
						</Box>
					</Inline>
					<BulkIssueSelector
						initialIssues={initialIssues}
						title={formatMessage(messages.jiraIssueAssignSelectIssuesTitle)}
						onIssuesSelected={handleSelectIssue}
						readonly={allActionsAreReadonly}
					/>
				</Stack>
				<ActionCardFooter>
					<Inline space="space.100" alignInline="end">
						{FeatureGates.getExperimentValue(
							'rovo_tools_resumption_migration_exp',
							'isEnabled',
							false,
						) && (
							<Button
								appearance="subtle"
								isDisabled={allActionsAreReadonly}
								onClick={(_, e) => {
									handleCancel();
									sendAnalyticsEvent(
										e.update({
											actionSubjectId: 'rovoJiraIssueAssignCancel',
										}),
									);
								}}
							>
								{formatMessage(messages.jiraIssueAssignCancel)}
							</Button>
						)}
						<Button
							appearance="primary"
							type="submit"
							isDisabled={allActionsAreReadonly || !selectedIssues.length}
							isLoading={isLoading}
							onClick={(_, e) => {
								handleSubmit();
								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'rovoJiraIssueAssign',
									}),
								);
							}}
							interactionName="ai-mate.issue-bulk-assign"
						>
							{formatMessage(messages.jiraIssueAssignUpdateSelectedIssues)}
						</Button>
					</Inline>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};

export const JiraIssueAssignAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraIssueAssign>,
): JSX.Element => {
	return <JiraIssueAssignActionNewLayout {...props} />;
};
